import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Divider, ThemeProvider, IconButton, InputAdornment } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Eye, EyeOff } from 'lucide-react';
import { useSignIn, useSignInWithGoogle } from '@lingaai/shared-hooks';
import { lightTheme } from '@lingaai/shared-theme';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const signIn = useSignIn();
  const signInWithGoogle = useSignInWithGoogle();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await signIn.mutateAsync({ email, password });
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle.mutateAsync();
      // User will be redirected to Google, then back to /dashboard
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Animated geometric background */}
        <Box
          component="svg"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 0.6,
          }}
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#174a43" stopOpacity="1">
                <animate attributeName="stop-color" values="#174a43; #22C55E; #174a43" dur="15s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#22C55E" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#22C55E; #10B981; #22C55E" dur="15s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="1">
                <animate attributeName="stop-color" values="#F59E0B; #FCD34D; #F59E0B" dur="18s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#FCD34D" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#FCD34D; #F59E0B; #FCD34D" dur="18s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22C55E" stopOpacity="1">
                <animate attributeName="stop-color" values="#22C55E; #10B981; #22C55E" dur="20s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity="1">
                <animate attributeName="stop-color" values="#FCD34D; #D97706; #FCD34D" dur="16s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#D97706" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <polygon className="polygon1" points="0,0 600,100 400,500" fill="url(#gradient1)" opacity="0.15" />
          <polygon className="polygon2" points="1200,0 800,300 1200,600" fill="url(#gradient2)" opacity="0.12" />
          <polygon className="polygon3" points="200,800 700,600 300,400" fill="url(#gradient3)" opacity="0.1" />
          <polygon className="polygon4" points="1000,800 1200,500 800,700" fill="url(#gradient4)" opacity="0.08" />
        </Box>
        <style>
          {`
          @keyframes morph1 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(50px, -30px); }
          }
          @keyframes morph2 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-40px, 40px); }
          }
          @keyframes morph3 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(30px, 50px); }
          }
          @keyframes morph4 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-30px, -40px); }
          }
          .polygon1 { animation: morph1 20s ease-in-out infinite; }
          .polygon2 { animation: morph2 25s ease-in-out infinite; }
          .polygon3 { animation: morph3 30s ease-in-out infinite; }
          .polygon4 { animation: morph4 22s ease-in-out infinite; }
        `}
        </style>
        <Card sx={{ p: 4, maxWidth: 400, width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Logo and Company Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Box component="img" src="/logo.svg" alt="Linga AI Logo" sx={{ width: 48, height: 48 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Linga AI
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ mb: 0 }}>
            Sign In
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button fullWidth variant="contained" type="submit" disabled={signIn.isPending} sx={{ mt: 2 }}>
              {signIn.isPending ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button fullWidth variant="outlined" onClick={handleGoogleSignIn} disabled={signInWithGoogle.isPending} startIcon={<GoogleIcon />}>
            Sign in with Google
          </Button>

          <Typography sx={{ mt: 2 }}>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </Typography>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
